/**
 * 쿠폰형(기프티콘) 정산 API 호출 함수 모음.
 *
 * URL prefix(`/{role}/...`)는 호출자 role에 따라 갈리고, 백엔드가 그 role로
 * 조회 범위를 강제한다. 조회는 admin·상조·마리 셋이 공유하고, 쓰기(집행·수정·삭제)는
 * admin 전용이다. 타입은 {@link ./couponSettlement.interface} 참고.
 */
import { GiftAccountConfig } from '@shared/api/bugoBrand/bugoBrand.interface';
import { queryString } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import {
  CouponPendingSummaryResponse,
  CouponRefundPendingRow,
  CouponSendPendingRow,
  GiftCouponSettleBody,
  GiftCouponSettleByFilterBody,
  GiftCouponSettleByFilterResult,
  GiftCouponSettleResult,
  GiftCouponSettlement,
  GiftCouponSettlementDetailResponse,
  GiftCouponSettlementLineEditResult,
  GiftCouponSettlementPatchBody,
  GiftCouponSettlementSummaryResponse,
  GiftSettlementStatus,
} from './couponSettlement.interface';

/**
 * 쿠폰 정산 화면의 상조 선택 드롭다운용 경량 옵션.
 * `/admin/bugo-brand` 응답에서 필요한 필드만 구조적으로 좁혀 쓴다.
 */
export interface GiftBugoBrandOption {
  _id: string;
  bugoAgency?: { corpName?: string; nickName?: string };
  giftAccountConfig?: GiftAccountConfig;
}

/**
 * 관리자용 상조 목록 조회 (쿠폰/배송 정산의 상조 선택기용).
 * 정산 대상 필터(giftAccountConfig.enabled)는 호출부에서 적용한다.
 */
export const listGiftBugoBrands = async (): Promise<GiftBugoBrandOption[]> => {
  const { data } = await api_.get<GiftBugoBrandOption[]>(
    `/${LowerRole.Admin}/bugo-brand?${queryString({
      options: { sort: { 'bugoAgency.corpName': 1 } },
    })}`,
  );
  return data;
};

/**
 * 조회 가능한 role. URL prefix가 되며 백엔드가 이 role로 범위를 스코프한다.
 * - `Admin`: 전체 정산 조회 + 쓰기 전부
 * - `BugoAgencyAdmin`: 본인 상조 정산만 (백엔드 owningFilter)
 * - `GiftAgent`(마리): 마리 경유(viaMari) 상조 건만 백엔드에서 스코프
 */
export type GiftCouponSettlementRole =
  | LowerRole.Admin
  | LowerRole.BugoAgencyAdmin
  | LowerRole.GiftAgent;

/**
 * 미정산(pending) 조회 파라미터.
 * admin은 `bugoBrand`를 지정하고, 상조·마리는 생략한다(서버가 본인 상조로 스코프 강제).
 */
export interface CouponPendingParams {
  bugoBrand?: string;
  /** 활동월 시작 ISO 문자열 (예: 2026-05-01T00:00:00+09:00) */
  from?: string;
  /** 활동월 종료 ISO 문자열 */
  to?: string;
}

// (구 조회 API — 목록/단건/count·pending/send·refund 깊은 populate 버전 — 는
// 단계별 로드 전환으로 소비처가 없어져 제거했다. 대체: 아래 summary/export/detail.)

/** {@link CouponPendingParams}를 비어있지 않은 값만 골라 쿼리스트링으로 만든다. */
const buildPendingQuery = (params: CouponPendingParams): string => {
  const sp = new URLSearchParams();
  if (params.bugoBrand) sp.set('bugoBrand', params.bugoBrand);
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  return sp.toString();
};

// ===== 단계별 로드(통계 → 상세) 읽기 API =====
// 설계: docs/gift-coupon-settlement-staged-api-spec.md. 페이지 진입 시엔 경량 summary만,
// raw 행(export/detail)은 상세보기(상조 선택·모달)에서만 불러 로드를 분산한다.

/** 미정산 요약 — 상조별 발송/환불 분리 집계 + netAmount. 페이지 진입 시 1회. */
export const getCouponPendingSummary = async (
  params: CouponPendingParams = {},
  role: GiftCouponSettlementRole = LowerRole.Admin,
): Promise<CouponPendingSummaryResponse> => {
  const { data } = await api_.get<CouponPendingSummaryResponse>(
    `/${role}/gift-coupon-settlement/pending/summary?${buildPendingQuery(params)}`,
  );
  return data;
};

/** 미정산 발송 raw 행 (평탄화·표시용 확정값). admin은 bugoBrand 지정 권장. */
export const getCouponSendPendingRows = async (
  params: CouponPendingParams = {},
  role: GiftCouponSettlementRole = LowerRole.Admin,
): Promise<CouponSendPendingRow[]> => {
  const { data } = await api_.get<CouponSendPendingRow[]>(
    `/${role}/gift-coupon-settlement/pending/send/export?${buildPendingQuery(params)}`,
  );
  return data;
};

/** 미정산 환불 raw 행 (평탄화·표시용 확정값). admin은 bugoBrand 지정 권장. */
export const getCouponRefundPendingRows = async (
  params: CouponPendingParams = {},
  role: GiftCouponSettlementRole = LowerRole.Admin,
): Promise<CouponRefundPendingRow[]> => {
  const { data } = await api_.get<CouponRefundPendingRow[]>(
    `/${role}/gift-coupon-settlement/pending/refund/export?${buildPendingQuery(params)}`,
  );
  return data;
};

/** 정산서 목록 요약 조회 파라미터 (admin만 bugoBrand 사용) */
export interface CouponSettlementSummaryParams {
  year?: number;
  status?: GiftSettlementStatus;
  bugoBrand?: string;
}

/** 정산서 목록 요약 — lines 미포함, 라인 집계는 서버 계산. 정렬 연·월 내림차순. */
export const getGiftCouponSettlementSummary = async (
  params: CouponSettlementSummaryParams = {},
  role: GiftCouponSettlementRole = LowerRole.Admin,
): Promise<GiftCouponSettlementSummaryResponse> => {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set('year', String(params.year));
  if (params.status) sp.set('status', params.status);
  if (params.bugoBrand) sp.set('bugoBrand', params.bugoBrand);
  const { data } = await api_.get<GiftCouponSettlementSummaryResponse>(
    `/${role}/gift-coupon-settlement/summary?${sp.toString()}`,
  );
  return data;
};

/** 정산서 상세 — 요약 + 평탄화 라인 행(발송 asc → 환불 asc). 상세 모달·엑셀용. */
export const getGiftCouponSettlementDetail = async (
  id: string,
  role: GiftCouponSettlementRole = LowerRole.Admin,
): Promise<GiftCouponSettlementDetailResponse> => {
  const { data } = await api_.get<GiftCouponSettlementDetailResponse>(
    `/${role}/gift-coupon-settlement/${id}/detail`,
  );
  return {
    ...data,
    // 환불 라인 부호 방어 — 계약상 서버가 음수로 주지만, overrideAmount 수기 라인 등
    // 검증을 안 거친 경로로 양수가 저장됐어도 표시·엑셀이 −로 일관되게 나오게 한다
    rows: data.rows.map((r) => (r.isRefund ? { ...r, amount: -Math.abs(r.amount) } : r)),
  };
};

// ===== 집행 (Admin 전용) =====

/**
 * 선택한 발송·환불 쿠폰으로 정산서를 생성·집행한다 (Admin 전용).
 * 제외된 쿠폰은 응답의 `skipped`에 사유와 함께 담겨 온다.
 */
export const settleGiftCoupon = async (
  body: GiftCouponSettleBody,
): Promise<GiftCouponSettleResult> => {
  const { data } = await api_.post<GiftCouponSettleResult>(
    `/${LowerRole.Admin}/gift-coupon-settlement/settle`,
    body,
  );
  return data;
};

/**
 * 조건 기반 정산 집행 (Admin 전용) — settle의 대용량(월 수천 건) 대안 (스펙: docs §5-1).
 * ID 열거 대신 (상조 + 활동일 기간)으로 서버가 pending을 재조회해 대상을 확정하고,
 * 프론트는 제외할 쿠폰만 열거한다. expected* 불일치 시 "…달라졌습니다" 에러로 거부
 * — 호출부는 재조회 안내.
 */
export const settleGiftCouponByFilter = async (
  body: GiftCouponSettleByFilterBody,
): Promise<GiftCouponSettleByFilterResult> => {
  const { data } = await api_.post<GiftCouponSettleByFilterResult>(
    `/${LowerRole.Admin}/gift-coupon-settlement/settle/by-filter`,
    body,
  );
  return data;
};

// ===== 수정 (Admin 전용) =====

/**
 * 쿠폰정산 문서 수정 (Admin 전용).
 * 상태 전환(Draft↔Paid), 라인 추가·제거, 금액 동결 등에 쓴다.
 */
export const patchGiftCouponSettlement = async (
  id: string,
  body: GiftCouponSettlementPatchBody,
): Promise<GiftCouponSettlement> => {
  const { data } = await api_.patch<GiftCouponSettlement>(
    `/${LowerRole.Admin}/gift-coupon-settlement/${id}`,
    body,
  );
  return data;
};

/**
 * 쿠폰정산 라인 추가·제거 (Admin 전용, Draft만) — 같은 PATCH지만 라인 편집을 보내면
 * 응답이 재계산 결과 래퍼({@link GiftCouponSettlementLineEditResult})로 온다.
 * 반려분은 `skipped`에 사유와 함께 담긴다 (settle과 동일 검증·쿼터 가드).
 */
export const patchGiftCouponSettlementLines = async (
  id: string,
  body: Pick<
    GiftCouponSettlementPatchBody,
    | 'addSendCouponIds'
    | 'removeSendCouponIds'
    | 'addRefundCouponIds'
    | 'removeRefundCouponIds'
    | 'allowOverQuotaCouponIds'
  >,
): Promise<GiftCouponSettlementLineEditResult> => {
  const { data } = await api_.patch<GiftCouponSettlementLineEditResult>(
    `/${LowerRole.Admin}/gift-coupon-settlement/${id}`,
    body,
  );
  return data;
};

/**
 * 쿠폰정산 라인 재계산 (Admin 전용, Draft만) — 라인 구성은 그대로 두고 현재 반올림
 * 규칙(수수료 건별 올림·지급액 건별 내림, 2026-07-24 개정)으로 금액을 재동결한다.
 * 개정 전에 집계된 Draft의 표시·지급 금액을 새 규칙과 일치시키는 관리자 도구.
 * @returns 재계산 결과 래퍼. 미지원 구서버(플래그 무시, 문서만 반환)면 `null`.
 */
export const recomputeGiftCouponSettlement = async (
  id: string,
): Promise<GiftCouponSettlementLineEditResult | null> => {
  const { data } = await api_.patch<
    GiftCouponSettlementLineEditResult | GiftCouponSettlement
  >(`/${LowerRole.Admin}/gift-coupon-settlement/${id}`, { recomputeLines: true });
  // 신서버는 라인편집 래퍼(includedSend 포함), 구서버는 문서 그대로 — 래퍼 유무로 판별
  return data && typeof (data as any).includedSend === 'number'
    ? (data as GiftCouponSettlementLineEditResult)
    : null;
};

// ===== 삭제 (Admin 전용, Draft만) =====

/** 쿠폰정산 문서 삭제 (Admin 전용). 백엔드는 Draft 상태만 허용한다. */
export const deleteGiftCouponSettlement = async (id: string): Promise<void> => {
  await api_.delete(`/${LowerRole.Admin}/gift-coupon-settlement/${id}`);
};
